import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { DropdownSelect } from './DropdownSelect'
import { useEscapeLayer } from './escapeLayer'
import { datePickerLabel, datePickerText, popupOwnership, triggerName } from './datePickerLabel'

/**
 * Custom calendar date picker — the design-system replacement for a native
 * `<input type="date">`, whose popup the browser owns and refuses to restyle
 * (mismatched padding, fonts, and control heights). This renders the calendar
 * itself so it matches the app: the trigger clones `DropdownSelect`'s button,
 * the month + year selectors ARE `DropdownSelect` (so their height / font are
 * identical to every other dropdown), and the day grid uses the same
 * brand-orange / gray tokens. Portal positioning, outside-click close, and
 * scroll/resize reflow mirror `DropdownSelect`.
 *
 * Value contract: ISO `YYYY-MM-DD` (or '' when empty), the same string a native
 * date input emits, so callers swap it in without touching their state. All
 * date math is LOCAL (parse at local midnight, format via `en-CA`) to avoid the
 * UTC off-by-one that bites UTC+ timezones like Nepal.
 */

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const PANEL_WIDTH = 320 // w-80
const YEAR_SPAN = 10
const EDGE_GAP = 8
const TRIGGER_GAP = 4
const ESTIMATED_PANEL_HEIGHT = 380

const toIso = (d: Date) => d.toLocaleDateString('en-CA') // YYYY-MM-DD, local
const parseIso = (iso: string): Date | null => {
  if (!iso) return null
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const addDays = (d: Date, n: number) => {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

interface DatePickerProps {
  value: string
  onChange: (iso: string) => void
  min?: string
  max?: string
  placeholder?: string
  size?: 'sm' | 'md'
  hasError?: boolean
  disabled?: boolean
  clearable?: boolean
  /** Classes for the WRAPPER, so width and layout only. Height comes from
   *  `size` (sm = 32px, md = 36px): a `h-8` passed here sizes the wrapper and
   *  leaves the trigger button at its own height, which is how fifteen call
   *  sites ended up 36px tall in a row of 32px filters. */
  className?: string
  leftIcon?: ReactNode
  /** Month the calendar opens on while nothing is selected (ISO). Defaults to
   *  today; a date-of-birth picker passes a date decades back so the operator
   *  is not paging through the current year. */
  defaultViewDate?: string
  /** Accessible name for the trigger (e.g. "Check-in from"). */
  ariaLabel?: string
  id?: string
  /** Optional field chrome, mirroring the old DateInput so it drops in 1:1. A
   *  trailing `*` in the label (or `required`) renders a red required asterisk. */
  label?: string
  error?: string
  hint?: string
  required?: boolean
}

/** Render a label string, turning a trailing `*` (or `required`) into a red
 *  required marker. */
function renderLabel(text: string, required?: boolean): ReactNode {
  const trimmed = text.trimEnd()
  const hasStar = trimmed.endsWith('*')
  if (!hasStar && !required) return text
  const base = hasStar ? trimmed.slice(0, -1).trimEnd() : trimmed
  return (
    <>
      {base}
      <span className="ml-0.5 text-red-500">*</span>
    </>
  )
}

interface DropPos {
  left: number
  placement: 'top' | 'bottom'
  top: number
  bottom: number
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select date…',
  size = 'md',
  hasError = false,
  disabled = false,
  clearable = false,
  className,
  leftIcon = <Calendar size={size === 'sm' ? 13 : 14} />,
  defaultViewDate,
  ariaLabel,
  id,
  label,
  error,
  hint,
  required,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const [dropPos, setDropPos] = useState<DropPos>({
    top: 0,
    left: 0,
    bottom: 0,
    placement: 'bottom',
  })
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = `${useId()}-calendar`
  const focusedBtnRef = useRef<HTMLButtonElement>(null)

  const selectedDate = useMemo(() => parseIso(value), [value])
  const minDate = useMemo(() => parseIso(min ?? ''), [min])
  const maxDate = useMemo(() => parseIso(max ?? ''), [max])
  const todayDate = useMemo(() => startOfDay(new Date()), [])
  const emptyAnchor = useMemo(
    () => parseIso(defaultViewDate ?? '') ?? todayDate,
    [defaultViewDate, todayDate],
  )

  // The month currently shown, and the day the keyboard cursor sits on.
  const [view, setView] = useState(() => selectedDate ?? emptyAnchor)
  const [focused, setFocused] = useState(() => selectedDate ?? emptyAnchor)

  // When the popover opens, jump the view + cursor to the selected date (or
  // today) so the user always lands on a meaningful month.
  useEffect(() => {
    if (!open) return
    const anchor = selectedDate ?? emptyAnchor
    setView(anchor)
    setFocused(anchor)
  }, [open, selectedDate, emptyAnchor])

  const isDisabledDay = (d: Date) =>
    (minDate != null && startOfDay(d) < startOfDay(minDate)) ||
    (maxDate != null && startOfDay(d) > startOfDay(maxDate))

  function recompute() {
    if (!btnRef.current) return
    const r = btnRef.current.getBoundingClientRect()
    const left = Math.max(EDGE_GAP, Math.min(r.left, window.innerWidth - PANEL_WIDTH - EDGE_GAP))

    const panelHeight = panelRef.current?.getBoundingClientRect().height ?? ESTIMATED_PANEL_HEIGHT
    const spaceBelow = window.innerHeight - r.bottom
    const spaceAbove = r.top
    const placement: 'top' | 'bottom' =
      spaceBelow >= panelHeight + TRIGGER_GAP || spaceBelow >= spaceAbove ? 'bottom' : 'top'

    if (placement === 'bottom') {
      setDropPos({ top: r.bottom + TRIGGER_GAP, left, bottom: 0, placement })
    } else {
      setDropPos({ top: 0, left, bottom: window.innerHeight - r.top + TRIGGER_GAP, placement })
    }
  }

  function handleOpen() {
    if (disabled) return
    setOpen((v) => !v)
  }
  // Position once the panel is mounted so recompute() can read its real
  // height. Runs before paint, so there is no flash at the initial (0,0).
  useLayoutEffect(() => {
    if (open) recompute()
  }, [open])
  const trackedPortalsRef = useRef<Set<Node>>(new Set())
  useEffect(() => {
    if (!open) return
    const portals = trackedPortalsRef.current
    portals.clear()
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n instanceof HTMLElement && n !== panelRef.current) portals.add(n)
        })
        m.removedNodes.forEach((n) => portals.delete(n))
      }
    })
    observer.observe(document.body, { childList: true })
    return () => {
      observer.disconnect()
      portals.clear()
    }
  }, [open])
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      const t = e.target as Node | null
      if (!t) return
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return
      for (const node of trackedPortalsRef.current) {
        if (node.contains(t)) return
      }
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    window.addEventListener('scroll', recompute, true)
    window.addEventListener('resize', recompute)
    return () => {
      window.removeEventListener('scroll', recompute, true)
      window.removeEventListener('resize', recompute)
    }
  }, [open])

  // Escape closes the calendar only, never a dialog the picker sits in, and
  // puts focus back on the trigger. A month or year list open inside the
  // calendar registers above this layer and takes the first Escape.
  useEscapeLayer(open, () => {
    setOpen(false)
    btnRef.current?.focus()
  })

  // Keep the focused day cell focused as the cursor moves (roving tabindex).
  useLayoutEffect(() => {
    if (open) focusedBtnRef.current?.focus()
  }, [open, focused])

  // Year range. An explicit min/max is treated as a HARD bound so a
  // date-of-birth picker (min = 120 years ago, max = today) offers the full
  // ~120-year span, while an unbounded picker just shows a window around the
  // relevant years. The current view / selection / today are always included so
  // the dropdown can never omit the year actually being shown.
  const yearOptions = useMemo(() => {
    const anchors = [todayDate.getFullYear(), view.getFullYear()]
    if (selectedDate) anchors.push(selectedDate.getFullYear())
    let lo = minDate ? minDate.getFullYear() : Math.min(...anchors) - YEAR_SPAN
    let hi = maxDate ? maxDate.getFullYear() : Math.max(...anchors) + YEAR_SPAN
    lo = Math.min(lo, ...anchors)
    hi = Math.max(hi, ...anchors)
    const out: { value: string; label: string }[] = []
    // Newest year first: for a DoB the recent years are the common picks, and a
    // 120-row list is quicker to use top-down than scrolling up from 1905.
    for (let y = hi; y >= lo; y--) out.push({ value: String(y), label: String(y) })
    return out
  }, [todayDate, selectedDate, minDate, maxDate, view])

  const monthOptions = useMemo(() => MONTHS.map((m, i) => ({ value: String(i), label: m })), [])

  // 6 weeks × 7 days, starting on the Sunday on/before the 1st — a fixed 42
  // cells so the panel height never jumps between months.
  const days = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1)
    const start = addDays(first, -first.getDay())
    return Array.from({ length: 42 }, (_, i) => addDays(start, i))
  }, [view])

  function commit(d: Date) {
    if (isDisabledDay(d)) return
    onChange(toIso(d))
    setOpen(false)
    btnRef.current?.focus()
  }

  function onGridKeyDown(e: React.KeyboardEvent) {
    let next: Date | null = null
    switch (e.key) {
      case 'ArrowLeft':
        next = addDays(focused, -1)
        break
      case 'ArrowRight':
        next = addDays(focused, 1)
        break
      case 'ArrowUp':
        next = addDays(focused, -7)
        break
      case 'ArrowDown':
        next = addDays(focused, 7)
        break
      case 'Home':
        next = addDays(focused, -focused.getDay())
        break
      case 'End':
        next = addDays(focused, 6 - focused.getDay())
        break
      case 'PageUp':
        next = new Date(focused.getFullYear(), focused.getMonth() - 1, focused.getDate())
        break
      case 'PageDown':
        next = new Date(focused.getFullYear(), focused.getMonth() + 1, focused.getDate())
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        commit(focused)
        return
      default:
        return
    }
    e.preventDefault()
    setFocused(next)
    if (next.getMonth() !== view.getMonth() || next.getFullYear() !== view.getFullYear()) {
      setView(new Date(next.getFullYear(), next.getMonth(), 1))
    }
  }

  const valueLabel = value ? datePickerLabel(value) : null
  const valueTitle = valueLabel?.secondary ? datePickerText(value) : undefined

  const triggerBase =
    size === 'sm' ? 'h-8 px-3 text-xs rounded-lg' : 'h-9 px-3.5 text-sm rounded-lg'
  const showError = hasError || !!error

  const control = (
    <div
      className={cn('relative', className)}
      // QA15-FD14: the calendar is portalled to <body>; inside an aria-modal
      // dialog that put it outside the accessibility tree. aria-owns sits on
      // this wrapper, not the button: a button's children are presentational,
      // so owning the calendar from it would flatten the day grid.
      {...popupOwnership(open, panelId).wrapper}
    >
      <button
        ref={btnRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        {...popupOwnership(open, panelId).trigger}
        aria-label={triggerName(ariaLabel ?? label, value)}
        aria-invalid={showError || undefined}
        aria-required={required || undefined}
        className={cn(
          'flex w-full items-center gap-2 bg-white border transition-shadow duration-150',
          triggerBase,
          'focus:outline-none focus:border-brand-orange focus:shadow-input-focus',
          showError
            ? 'border-red-400'
            : open
              ? 'border-brand-orange shadow-input-focus'
              : 'border-gray-200 hover:border-gray-300',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        {leftIcon && <span className="shrink-0 text-gray-400">{leftIcon}</span>}
        <span
          className={cn('flex-1 truncate text-left', value ? 'text-brand-rail' : 'text-gray-400')}
          title={valueTitle}
        >
          {valueLabel ? (
            <>
              {valueLabel.primary}
              {valueLabel.secondary && (
                <span className="ml-1.5 text-gray-500">{valueLabel.secondary}</span>
              )}
            </>
          ) : (
            placeholder
          )}
        </span>
        <ChevronDown
          size={14}
          className={cn('shrink-0 text-gray-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-label="Choose date"
            className="fixed z-[9999] w-80 rounded-xl border border-gray-100 bg-white p-3 shadow-lg"
            style={{
              left: dropPos.left,
              ...(dropPos.placement === 'bottom'
                ? { top: dropPos.top }
                : { bottom: dropPos.bottom }),
              maxHeight: `calc(100vh - ${EDGE_GAP * 2}px)`,
              overflowY: 'auto',
            }}
          >
            {/* Month + year navigation — the selects ARE DropdownSelect, so
                their height and font match the rest of the form. */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
                className="flex h-8 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <div className="flex-1">
                <DropdownSelect
                  size="sm"
                  ariaLabel="Month"
                  value={String(view.getMonth())}
                  onChange={(v) => setView(new Date(view.getFullYear(), Number(v), 1))}
                  options={monthOptions}
                />
              </div>
              <div className="w-[88px] shrink-0">
                <DropdownSelect
                  size="sm"
                  ariaLabel="Year"
                  value={String(view.getFullYear())}
                  onChange={(v) => setView(new Date(Number(v), view.getMonth(), 1))}
                  options={yearOptions}
                />
              </div>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
                className="flex h-8 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            {/* Weekday header */}
            <div className="mt-3 grid grid-cols-7 gap-0.5">
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  aria-hidden
                  className="flex h-6 items-center justify-center text-[11px] font-medium text-gray-400"
                >
                  {w}
                </div>
              ))}
            </div>

            {/* Day grid — keydown is handled on the grid container (roving
                tabindex); the day cells below are the interactive elements. */}
            <div role="grid" className="mt-0.5 grid grid-cols-7 gap-0.5" onKeyDown={onGridKeyDown}>
              {days.map((d) => {
                const inMonth = d.getMonth() === view.getMonth()
                const isSelected = selectedDate != null && sameDay(d, selectedDate)
                const isToday = sameDay(d, todayDate)
                const isFocused = sameDay(d, focused)
                const disabledDay = isDisabledDay(d)
                return (
                  <button
                    key={toIso(d)}
                    ref={isFocused ? focusedBtnRef : undefined}
                    type="button"
                    role="gridcell"
                    tabIndex={isFocused ? 0 : -1}
                    disabled={disabledDay}
                    aria-selected={isSelected}
                    aria-label={datePickerText(toIso(d))}
                    onClick={() => commit(d)}
                    className={cn(
                      'flex h-8 items-center justify-center rounded-lg text-xs tabular-nums transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange/40',
                      disabledDay && 'cursor-not-allowed text-gray-300',
                      !disabledDay && isSelected && 'bg-brand-orange font-semibold text-white',
                      !disabledDay &&
                        !isSelected &&
                        inMonth &&
                        'text-brand-rail hover:bg-brand-orange/5',
                      !disabledDay && !isSelected && !inMonth && 'text-gray-300 hover:bg-gray-50',
                      !isSelected && isToday && !disabledDay && 'font-semibold text-brand-orange',
                    )}
                  >
                    {d.getDate()}
                  </button>
                )
              })}
            </div>

            {/* Footer: Today + optional Clear */}
            <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-2">
              <button
                type="button"
                onClick={() => commit(todayDate)}
                disabled={isDisabledDay(todayDate)}
                className="rounded-lg px-2 py-1 text-xs font-semibold text-brand-orange hover:bg-brand-orange/5 transition-colors disabled:cursor-not-allowed disabled:text-gray-300"
              >
                Today
              </button>
              {clearable && value && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('')
                    setOpen(false)
                    btnRef.current?.focus()
                  }}
                  className="rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )

  // Bare control unless field chrome (label / error / hint) was requested, so
  // it drops into either a "control-only" call site or a labelled one.
  if (!label && !error && !hint) return control

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {renderLabel(label, required)}
        </label>
      )}
      {control}
      {error && (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  )
}
